import {
  INVITATION_STATE,
  normalizeInvitationEmail,
  normalizeInvitationState,
} from "./invitationIdentity";
import { isPendingInvitationClient } from "./invitedClientUiGuards";

const TRIAL_WINDOW_DAYS = 10;
const MAX_ACTIVE_CLIENTS_FOR_NUDGE = 2;
const DESKTOP_SCREEN_SIZE = "large";

const toDate = (value) => {
  if (value instanceof Date) {
    return new Date(value.getTime());
  }
  return new Date(value);
};

const getActiveClientsCount = (coach) => {
  if (Array.isArray(coach?.activeClients)) {
    return coach.activeClients.length;
  }

  if (!Array.isArray(coach?.clients)) {
    return 0;
  }

  return coach.clients.filter((client) => client?.status === "active-string")
    .length;
};

export const isCoachInTrialWindow = ({
  subscriptionStatus,
  trialPeriodStartDate,
  now = new Date(),
  trialWindowDays = TRIAL_WINDOW_DAYS,
} = {}) => {
  if (subscriptionStatus === "trialing") {
    return true;
  }

  const nowDate = toDate(now);
  const trialStartDate = toDate(trialPeriodStartDate);
  if (!Number.isFinite(nowDate.getTime())) {
    return false;
  }
  if (!Number.isFinite(trialStartDate.getTime())) {
    return false;
  }

  trialStartDate.setDate(trialStartDate.getDate() + trialWindowDays);
  return nowDate <= trialStartDate;
};

export const isCoachEligibleForInviteBlockNudge = ({
  coach,
  isMobile,
  screenSize,
  now = new Date(),
} = {}) => {
  if (!coach || typeof coach !== "object") {
    return false;
  }

  if (isMobile === true) {
    return false;
  }

  if (typeof screenSize === "string" && screenSize !== DESKTOP_SCREEN_SIZE) {
    return false;
  }

  const activeClientsCount = getActiveClientsCount(coach);
  if (activeClientsCount > MAX_ACTIVE_CLIENTS_FOR_NUDGE) {
    return false;
  }

  return isCoachInTrialWindow({
    subscriptionStatus: coach?.subscriptionStatus,
    trialPeriodStartDate: coach?.trialPeriodStartDate,
    now,
  });
};

export const resolveLatestSuccessfullySentInvitationEmail = ({
  submittedEmails,
  sentEmails,
} = {}) => {
  const normalizedSubmittedEmails = Array.isArray(submittedEmails)
    ? submittedEmails
        .map((email) => normalizeInvitationEmail(email))
        .filter(Boolean)
    : [];
  const normalizedSentEmails = Array.isArray(sentEmails)
    ? sentEmails.map((email) => normalizeInvitationEmail(email)).filter(Boolean)
    : [];

  if (normalizedSubmittedEmails.length === 0 || normalizedSentEmails.length === 0) {
    return "";
  }

  const sentSet = new Set(normalizedSentEmails);
  for (let i = normalizedSubmittedEmails.length - 1; i >= 0; i -= 1) {
    const candidate = normalizedSubmittedEmails[i];
    if (sentSet.has(candidate)) {
      return candidate;
    }
  }

  return normalizedSentEmails[normalizedSentEmails.length - 1] ?? "";
};

export const resolveInviteBlockNudgeTargetClient = ({
  clients,
  invitationEmail,
} = {}) => {
  const normalizedInvitationEmail = normalizeInvitationEmail(invitationEmail);
  if (!normalizedInvitationEmail) {
    return null;
  }
  if (!Array.isArray(clients) || clients.length === 0) {
    return null;
  }

  const candidates = clients.filter((client) => {
    if (!isPendingInvitationClient(client)) {
      return false;
    }

    const clientInvitationEmail = normalizeInvitationEmail(
      client?.invitationEmail ?? client?.email
    );
    return clientInvitationEmail === normalizedInvitationEmail;
  });

  if (candidates.length === 0) {
    return null;
  }

  const sortedCandidates = [...candidates].sort((a, b) => {
    const aCreatedAt =
      Number.isFinite(Number(a?.invitationCreatedAtMs))
        ? Number(a.invitationCreatedAtMs)
        : 0;
    const bCreatedAt =
      Number.isFinite(Number(b?.invitationCreatedAtMs))
        ? Number(b.invitationCreatedAtMs)
        : 0;
    return bCreatedAt - aCreatedAt;
  });

  return sortedCandidates[0] ?? null;
};

export const hasPendingOrAcceptingInvitationSummaryForEmail = ({
  invitationSummaries,
  invitationEmail,
} = {}) => {
  const normalizedInvitationEmail = normalizeInvitationEmail(invitationEmail);
  if (!normalizedInvitationEmail) {
    return false;
  }
  if (!Array.isArray(invitationSummaries) || invitationSummaries.length === 0) {
    return false;
  }

  return invitationSummaries.some((summary) => {
    const summaryEmail = normalizeInvitationEmail(summary?.invitationEmail);
    if (summaryEmail !== normalizedInvitationEmail) {
      return false;
    }

    const summaryState = normalizeInvitationState(summary?.state);
    return (
      summaryState === INVITATION_STATE.PENDING ||
      summaryState === INVITATION_STATE.ACCEPTING
    );
  });
};
